import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { CharitableTrustDesignerTrowser } from '@/modules/entities/charitableTrusts/CharitableTrustDesignerTrowser/CharitableTrustDesignerTrowser';
import { inKindTransferToggleCopy } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { CharitableTrustDesignerStages } from '@/navigation/constants';
import { formatCurrency } from '@/utils/formatting/currency';

import { calculateTotalValue } from '../../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.utils';
import { SelectedAssetDetails } from '../../EstateWaterfallHypotheticalTransfers/SelectedAssetDetails';
import { TransferFundingSource } from '../../EstateWaterfallHypotheticalTransfers/TransferFundingSource';
import {
  HypotheticalCharitableTrustFormPaths,
  HypotheticalCharitableTrustFormShape,
  HypotheticalCharitableTrustModalKind,
  SourceValues,
} from '../EstateWaterfallHypotheticalCharitableTrustModal.types';
import { getHypotheticalCharitableTrustModalKindLabel } from '../EstateWaterfallHypotheticalCharitableTrustModal.utils';

export function TransferNameField() {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  return (
    <FormAwareTextInput<HypotheticalCharitableTrustFormShape>
      control={control}
      fieldName={
        'displayName' as const satisfies HypotheticalCharitableTrustFormPaths
      }
      label="Transfer name"
      required
      placeholder="Specify transfer name"
    />
  );
}

interface SimulatedProposalSelectFieldProps {
  householdId: string;
  proposalOptions: FormAwareTypeaheadSelectInputProps<HypotheticalCharitableTrustFormShape>['options'];
  charitableTrustModalKind: HypotheticalCharitableTrustModalKind;
}

export function SimulatedProposalSelectField({
  householdId,
  proposalOptions,
  charitableTrustModalKind,
}: SimulatedProposalSelectFieldProps) {
  const { control, setValue } =
    useFormContext<HypotheticalCharitableTrustFormShape>();

  const trustTypeLabel = getHypotheticalCharitableTrustModalKindLabel(
    charitableTrustModalKind
  );

  const selectedProposalId = useWatch({
    control,
    name: 'simulatedProposalId',
  });

  const [
    { isModalOpen: isDraftProposalModalOpen },
    { openModal: openDraftProposalModal, closeModal: closeDraftProposalModal },
  ] = useModalState();

  const handleFormAction = useCallback(
    (action: 'cancel' | CharitableTrustDesignerStages, proposalId?: string) => {
      if (proposalId) {
        setValue('simulatedProposalId', proposalId);
      }

      if (action === 'cancel') {
        closeDraftProposalModal();
        return;
      }

      if (action === CharitableTrustDesignerStages.BASIC_INFORMATION) {
        return;
      }

      if (
        action === CharitableTrustDesignerStages.MODEL_SCENARIOS &&
        !proposalId
      ) {
        return;
      }

      closeDraftProposalModal();
    },
    [closeDraftProposalModal, setValue]
  );

  const handleAddNewOption = () => {
    setValue('simulatedProposalId', '');
    openDraftProposalModal();
  };

  useEffect(() => {
    // While the designer is open we may be mid-creation, where a just-created
    // proposal isn't in `proposalOptions` until the list refetches. Clearing it
    // here would orphan the new proposal and break the analysis step's save.
    if (isDraftProposalModalOpen) return;
    if (!selectedProposalId) return;
    if (proposalOptions.find((o) => o.value === selectedProposalId)) return;

    setValue('simulatedProposalId', '');
  }, [isDraftProposalModalOpen, proposalOptions, selectedProposalId, setValue]);

  return (
    <>
      {isDraftProposalModalOpen && (
        <CharitableTrustDesignerTrowser
          isOpen={true}
          householdId={householdId}
          proposalId={selectedProposalId}
          variant={
            charitableTrustModalKind ===
            HypotheticalCharitableTrustModalKind.CRT
              ? 'crt'
              : 'clt'
          }
          onFormAction={handleFormAction}
        />
      )}
      <Stack direction="row" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <FormAwareTypeaheadSelectInput<HypotheticalCharitableTrustFormShape>
            control={control}
            fieldName={
              'simulatedProposalId' as const satisfies HypotheticalCharitableTrustFormPaths
            }
            label={`${trustTypeLabel} details`}
            groupBy={(o) => o.groupName ?? ''}
            required
            options={proposalOptions}
            addNewOption={{
              text: `Create new hypothetical ${trustTypeLabel}`,
              icon: <PlusIcon size={16} />,
              onClick: handleAddNewOption,
            }}
            placeholder={`Select existing ${trustTypeLabel} analysis or create new`}
            dropdownProps={{
              style: {
                width: 'auto',
                minWidth: 'calc(100% - 48px)',
                maxWidth: '400px',
              },
            }}
          />
        </Box>
        {selectedProposalId && (
          <Stack justifyContent="end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => openDraftProposalModal()}
            >
              Edit details
            </Button>
          </Stack>
        )}
      </Stack>
    </>
  );
}

interface SourceEntitySelectFieldProps {
  sourceEntityOptions: FormAwareTypeaheadSelectInputProps<HypotheticalCharitableTrustFormShape>['options'];
  sourceIndividualOptions: FormAwareTypeaheadSelectInputProps<HypotheticalCharitableTrustFormShape>['options'];
}

export function SourceEntitySelectField({
  sourceEntityOptions,
  sourceIndividualOptions,
}: SourceEntitySelectFieldProps) {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();
  const options = useMemo(() => {
    return [...sourceEntityOptions, ...sourceIndividualOptions];
  }, [sourceEntityOptions, sourceIndividualOptions]);

  return (
    <FormAwareTypeaheadSelectInput<HypotheticalCharitableTrustFormShape>
      control={control}
      fieldName={
        'sourceId' as const satisfies HypotheticalCharitableTrustFormPaths
      }
      label="From"
      groupBy={(o) => o.groupName ?? ''}
      required
      options={options}
      placeholder="Select funding source"
    />
  );
}

interface FundingRadioGroupFieldProps {
  sourceValues: Record<string, SourceValues>;
}

export function FundingRadioGroupField({
  sourceValues,
}: FundingRadioGroupFieldProps) {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  const [proRataPattern, sourceId] = useWatch({
    control,
    name: ['proRataPattern', 'sourceId'],
  });

  const assetOptions = useMemo(() => {
    const sourceValue = sourceValues[sourceId];

    if (!sourceValue) {
      return [];
    }

    return Object.entries(sourceValue.byClassId).map(([id, details]) => ({
      value: id,
      display: `${details.displayName} (${formatCurrency(details.totalValueForClass, { notation: 'compact' })})`,
      groupName:
        details.hasOwnershipStake === null
          ? 'Asset classes'
          : 'Business entities',
    }));
  }, [sourceId, sourceValues]);

  const totalSourceValue = sourceValues[sourceId]?.totalValue ?? new Decimal(0);

  return (
    <TransferFundingSource<HypotheticalCharitableTrustFormShape>
      control={control}
      displayKind="amount"
      proRataPattern={proRataPattern}
      sourceId={sourceId}
      sourceValue={totalSourceValue}
      assetOptions={assetOptions}
      helpTextAssetClassesAndBusinesses="Select a source entity to see available assets"
    />
  );
}

interface SelectedAssetDetailsFieldProps {
  sourceValues: Record<string, SourceValues>;
}

export function SelectedAssetDetailsField({
  sourceValues,
}: SelectedAssetDetailsFieldProps) {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  const [
    sourceId,
    assetsPattern,
    fundingKind,
    selectedAssetClassesAndBusinessIds,
    selectedAssetClassAndBusinessValues,
  ] = useWatch({
    control,
    name: [
      'sourceId',
      'assetsPattern',
      'fundingKind',
      'selectedAssetClassesAndBusinessIds',
      'selectedAssetClassAndBusinessValues',
    ],
  });

  const assetOptionDetails = sourceValues[sourceId];

  const selectedAssetDetails = useMemo(() => {
    if (!selectedAssetClassesAndBusinessIds || !assetOptionDetails) return [];
    return compact(
      selectedAssetClassesAndBusinessIds.map((id) => {
        const details = assetOptionDetails.byClassId[id];
        if (!details) return null;
        return {
          id,
          ...details,
        };
      })
    ).sort((a, b) => {
      return a.totalValueForClass.comparedTo(b.totalValueForClass);
    });
  }, [selectedAssetClassesAndBusinessIds, assetOptionDetails]);

  const totalSelectedValue = useMemo(() => {
    const classIdToValue: Record<
      string,
      {
        totalValue: Decimal;
      }
    > = {};

    Object.entries(assetOptionDetails?.byClassId ?? {}).forEach(
      ([classId, value]) => {
        classIdToValue[classId] = {
          totalValue: value.totalValueForClass,
        };
      }
    );

    return calculateTotalValue({
      selectedAssetIds: selectedAssetClassesAndBusinessIds,
      selectedAssetClassAndBusinessValues,
      assetOptionDetails: classIdToValue,
      pattern: assetsPattern,
    });
  }, [
    assetOptionDetails?.byClassId,
    assetsPattern,
    selectedAssetClassAndBusinessValues,
    selectedAssetClassesAndBusinessIds,
  ]);

  if (fundingKind === 'assets') {
    return (
      <SelectedAssetDetails
        control={control}
        fundingKind={fundingKind}
        selectedAssetDetails={selectedAssetDetails}
        assetsPattern={assetsPattern}
        totalSelectedValue={totalSelectedValue}
      />
    );
  }

  return null;
}

export function ForceLiquidationOnTransferField() {
  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  return (
    <FormAwareSwitch<HypotheticalCharitableTrustFormShape>
      labelPosition="right"
      fieldName={
        'forceLiquidationOnTransfer' as const satisfies HypotheticalCharitableTrustFormPaths
      }
      {...inKindTransferToggleCopy}
      control={control}
    />
  );
}
